import React from 'react';
import { Loader2, Lock } from 'lucide-react';
import { useAuth, useSupabase } from '../providers/SupabaseProvider';

interface SampleAccessGuardProps {
  children: React.ReactNode;
  allowAssistant?: boolean;
}

export default function SampleAccessGuard({ children, allowAssistant = false }: SampleAccessGuardProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadSampleAccess = async () => {
      if (!user) {
        if (isMounted) {
          setHasAccess(false);
          setLoading(false);
        }
        return;
      }

      try {
        const { data: orgUser, error } = await supabase
          .from('organization_users')
          .select('role, medical_role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;

        // Diese Prüfung steuert den Zugriff auf Proben-Seiten; optional kann die Assistenzrolle für ausgewählte Seiten erlaubt werden.
        const isOwner = (orgUser?.role || '').toLowerCase() === 'owner';
        const normalizedMedicalRole = (orgUser?.medical_role || '').toLowerCase();
        const isAssistant = ['assistant', 'assistenz'].includes(normalizedMedicalRole);
        const isEmployeeFileOnlyRole = normalizedMedicalRole === 'employee_file_only';
        const hasSampleAccess =
          Boolean(orgUser) &&
          (isOwner || (!isEmployeeFileOnlyRole && (allowAssistant || !isAssistant)));

        if (isMounted) {
          setHasAccess(hasSampleAccess);
        }
      } catch (error) {
        console.error('Fehler beim Prüfen des Probenzugriffs:', error);
        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSampleAccess();
    return () => {
      isMounted = false;
    };
  }, [allowAssistant, supabase, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <Lock className="h-10 w-10 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Kein Zugriff</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md">
          Benutzer mit der medizinischen Rolle &quot;assistant&quot; oder &quot;employee_file_only&quot; haben keinen Zugriff auf diese Proben-Seite.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
